(() => {
  // Deployment recovery marker: ship the validated PR #272 theme after its CI gate timed out.
  const STORAGE_KEY = 'transportPendingGripSelection';
  const MAX_AGE_MS = 2 * 60 * 60 * 1000;
  const GRIP_COLLECTION_PATH = '/collections/grip-tape';
  const SELECT_GRIP_PARAM = 'select_grip';
  const GRIP_PRODUCT_PARAM = 'grip_for';

  const getStorage = () => {
    try {
      return window.sessionStorage;
    } catch (_error) {
      return null;
    }
  };

  const clearPendingSelection = () => {
    getStorage()?.removeItem(STORAGE_KEY);
  };

  const readPendingSelection = () => {
    const rawValue = getStorage()?.getItem(STORAGE_KEY);
    if (!rawValue) return null;

    try {
      const pending = JSON.parse(rawValue);
      const isValid = Number.isInteger(pending.deckVariantId) &&
        pending.deckVariantId > 0 &&
        Number.isInteger(pending.quantity) &&
        pending.quantity > 0 &&
        typeof pending.deckTitle === 'string' &&
        typeof pending.deckUrl === 'string' &&
        Number.isFinite(pending.createdAt) &&
        Date.now() - pending.createdAt <= MAX_AGE_MS;
      if (isValid) return pending;
    } catch (_error) {
      // Invalid or stale state is cleared below.
    }

    clearPendingSelection();
    return null;
  };

  const writePendingSelection = (pending) => {
    const storage = getStorage();
    if (!storage) return false;
    storage.setItem(STORAGE_KEY, JSON.stringify(pending));
    return true;
  };

  const urlWithParam = (value, name, paramValue = '1') => {
    const url = new URL(value, window.location.origin);
    url.searchParams.set(name, paramValue);
    return `${url.pathname}${url.search}${url.hash}`;
  };

  const createNotice = (title, text, pending, className = '') => {
    const notice = document.createElement('div');
    notice.className = `grip-flow-notice ${className}`.trim();

    const heading = document.createElement('p');
    heading.className = 'grip-flow-notice__title';
    heading.textContent = title;

    const description = document.createElement('p');
    description.className = 'grip-flow-notice__text';
    description.textContent = text;

    const cancel = document.createElement('a');
    cancel.className = 'grip-flow-notice__cancel text-button';
    cancel.href = pending.deckUrl;
    cancel.textContent = 'Cancel and return to deck';
    cancel.addEventListener('click', () => clearPendingSelection());

    notice.append(heading);
    if (text) notice.append(description);
    notice.append(cancel);
    return notice;
  };

  const createBackToDeckLink = (pending) => {
    const link = document.createElement('a');
    link.className = 'grip-flow-back';
    link.href = pending.deckUrl;
    link.innerHTML = `
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M15 18 9 12l6-6" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"></path>
      </svg>
      <span>Back to deck page</span>
    `;
    link.addEventListener('click', () => clearPendingSelection());
    return link;
  };

  const setButtonText = (button, text) => {
    const label = button?.querySelector('.single-add-to-cart-button--text');
    if (label && label.textContent.trim() !== text) label.textContent = text;
  };

  const showFormError = (form, message) => {
    const wrapper = form.closest('product-form')?.querySelector('.product-form__error-message-wrapper');
    const text = wrapper?.querySelector('.product-form__error-message');
    if (wrapper && text) {
      text.textContent = message;
      wrapper.hidden = false;
      return;
    }
    window.alert(message);
  };

  const initDeckSelector = (selector) => {
    const form = document.getElementById(selector.dataset.productFormId);
    const gripMode = selector.querySelector('select[data-grip-mode]');
    if (!form || !gripMode) return;

    document.documentElement.classList.add('grip-selector-deck');
    const button = form.querySelector('.single-add-to-cart-button');
    const addToCartText = window.theme?.variantStrings?.addToCart || 'Add to cart';
    const includesGrip = () => gripMode.value === 'Yes';
    const syncButton = () => {
      if (!button || button.disabled) return;
      setButtonText(button, includesGrip() ? 'Choose Grip Tape' : addToCartText);
    };

    selector.addEventListener('change', (event) => {
      event.stopPropagation();
      syncButton();
    });
    document.addEventListener('product:variant-change', () => {
      window.setTimeout(syncButton);
    });
    if (button) {
      const buttonObserver = new MutationObserver(syncButton);
      buttonObserver.observe(button, { attributes: true, childList: true, subtree: true });
    }
    syncButton();

    form.addEventListener('submit', (event) => {
      if (!includesGrip()) return;

      const variantInput = form.querySelector('input[name="id"]');
      const deckVariantId = Number.parseInt(variantInput?.value || '', 10);
      if (!deckVariantId || variantInput?.disabled) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const quantityInput = form.querySelector('input[name="quantity"]');
      const quantity = Math.max(1, Number.parseInt(quantityInput?.value || '1', 10) || 1);
      const pending = {
        deckVariantId,
        quantity,
        deckTitle: selector.dataset.productTitle,
        deckUrl: selector.dataset.productUrl,
        createdAt: Date.now(),
      };

      if (!writePendingSelection(pending)) {
        showFormError(form, 'Your browser could not save the selected deck. Please enable session storage and try again.');
        return;
      }

      const collectionUrl = selector.dataset.gripCollectionUrl || GRIP_COLLECTION_PATH;
      window.location.assign(urlWithParam(collectionUrl, SELECT_GRIP_PARAM));
    }, true);
  };

  const decorateGripProductLinks = (container) => {
    container.querySelectorAll('a[href*="/products/"]').forEach((link) => {
      link.href = urlWithParam(link.href, GRIP_PRODUCT_PARAM);
    });
  };

  const initGripCollection = (pending) => {
    if (window.location.pathname !== GRIP_COLLECTION_PATH) return;
    if (new URLSearchParams(window.location.search).get(SELECT_GRIP_PARAM) !== '1') return;

    document.documentElement.classList.add('grip-selection-collection');
    const grid = document.querySelector('#ProductGridContainer');
    if (!grid) return;

    grid.prepend(createBackToDeckLink(pending));
    decorateGripProductLinks(grid);

    const gridObserver = new MutationObserver(() => decorateGripProductLinks(grid));
    gridObserver.observe(grid, { childList: true, subtree: true });

    grid.addEventListener('click', (event) => {
      const card = event.target.closest('product-card');
      const action = event.target.closest('button, quick-view');
      if (!card || !action) return;

      const productLink = card.querySelector('.product-featured-image-link, .product-card-title');
      if (!productLink) return;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      window.location.assign(urlWithParam(productLink.href, GRIP_PRODUCT_PARAM));
    }, true);
  };

  const createGroupId = () => {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    return `grip-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  };

  const createApplicationChoice = () => {
    const choice = document.createElement('div');
    choice.className = 'grip-application-choice';
    choice.innerHTML = `
      <label class="grip-application-choice__legend" for="GripApplicationSelect">Apply grip tape to board?</label>
      <div class="grip-application-choice__select-wrap">
        <select class="grip-application-choice__select" id="GripApplicationSelect" name="grip-application-choice" aria-required="true">
          <option value="">Select an option</option>
          <option value="Yes">Yes — Skateboard deck with grip tape applied cannot be returned.</option>
          <option value="No">No — Include it uninstalled.</option>
        </select>
      </div>
    `;
    return choice;
  };

  const initGripProduct = (pending) => {
    if (!window.location.pathname.startsWith('/products/')) return;
    if (new URLSearchParams(window.location.search).get(GRIP_PRODUCT_PARAM) !== '1') return;

    const productForm = Array.from(document.querySelectorAll('product-form')).find((element) => {
      return !element.dataset.sticky &&
        element.dataset.gripSelectorProduct === 'true' &&
        element.querySelector('form[data-type="add-to-cart-form"]');
    });
    const form = productForm?.querySelector('form[data-type="add-to-cart-form"]');
    if (!productForm || !form) return;

    document.documentElement.classList.add('grip-selection-product');
    const notice = createNotice(
      `Grip tape for ${pending.deckTitle}`,
      '',
      pending,
      'grip-flow-notice--product'
    );
    productForm.before(notice);

    const applicationChoice = createApplicationChoice();
    const addToCartHolder = form.querySelector('.add_to_cart_holder');
    if (!addToCartHolder) return;
    addToCartHolder.before(applicationChoice);
    applicationChoice.addEventListener('change', () => {
      const errorWrapper = productForm.querySelector('.product-form__error-message-wrapper');
      if (errorWrapper) errorWrapper.hidden = true;
    });

    const button = form.querySelector('.single-add-to-cart-button');
    const syncButton = () => {
      if (button && !button.disabled) setButtonText(button, 'Add Deck + Grip Tape');
    };
    document.addEventListener('product:variant-change', () => window.setTimeout(syncButton));
    if (button) {
      const buttonObserver = new MutationObserver(syncButton);
      buttonObserver.observe(button, { attributes: true, childList: true, subtree: true });
    }
    syncButton();

    form.addEventListener('submit', async (event) => {
      const gripVariantInput = form.querySelector('input[name="id"]');
      const gripVariantId = Number.parseInt(gripVariantInput?.value || '', 10);
      if (!gripVariantId || gripVariantInput?.disabled) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const applicationSelection = form.querySelector('select[name="grip-application-choice"]');
      if (!applicationSelection?.value) {
        showFormError(form, 'Please choose whether you would like us to apply the grip tape.');
        applicationSelection?.focus();
        return;
      }

      const gripTitle = document.querySelector('h1')?.textContent?.trim() || 'Selected grip tape';
      const groupId = createGroupId();
      const applyGripTape = applicationSelection.value;
      const items = [
        {
          id: pending.deckVariantId,
          quantity: pending.quantity,
          properties: {
            'Apply grip tape to board?': applyGripTape,
            'Selected Grip Tape': gripTitle,
            _grip_group: groupId,
          },
        },
        {
          id: gripVariantId,
          quantity: pending.quantity,
          properties: {
            'Apply grip tape to board?': applyGripTape,
            'For Deck': pending.deckTitle,
            _grip_group: groupId,
          },
        },
      ];

      button?.setAttribute('disabled', 'disabled');
      button?.setAttribute('aria-disabled', 'true');
      button?.classList.add('loading');

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ items }),
        });
        const result = await response.json();
        if (!response.ok) {
          throw new Error(result.description || result.message || 'The deck and grip tape could not be added.');
        }

        clearPendingSelection();
        window.location.assign('/cart');
      } catch (error) {
        showFormError(form, error.message || 'The deck and grip tape could not be added. Please try again.');
        button?.removeAttribute('disabled');
        button?.removeAttribute('aria-disabled');
        button?.classList.remove('loading');
        syncButton();
      }
    }, true);
  };

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('grip-selector-flow').forEach(initDeckSelector);

    const pending = readPendingSelection();
    if (!pending) return;
    initGripCollection(pending);
    initGripProduct(pending);
  });
})();
